import type { Attachment } from '../knowledgeBase/knowledgeBaseService';

const API_BASE = '/api';

const getToken = () => localStorage.getItem('token');

async function apiRequest(url: string, options: RequestInit = {}) {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {})
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(`${API_BASE}${url}`, { ...options, headers });

  if (!response.ok) {
    if (response.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/';
      throw new Error('未授权，请重新登录');
    }
    const err = await response.json().catch(() => ({ message: '请求失败' }));
    throw new Error(err.message || err.error || '请求失败');
  }

  if (response.status === 204) return { success: true };
  return response.json();
}

export type ExperimentDiscipline = 'chemistry' | 'biology' | 'materials' | 'general';
export type ExperimentStatus = 'draft' | 'final';

export interface ReagentRow {
  compound: string;
  molarity: string;
  volume: string;
}

export interface ChemistryBlock {
  objective: string;
  procedureOutline: string;
  reagentsAndSolvents: string;
  conditions: string;
  observations: string;
  workupAndAnalysis: string;
  /** 化学模板试剂表（后端持久化） */
  reagentRows?: ReagentRow[];
}

export interface CustomFieldRow {
  key: string;
  value: string;
}

export interface RelatedReservationSummary {
  _id: string;
  startTime?: string;
  endTime?: string;
  status?: string;
  purpose?: string;
  instrumentId?: string | { _id?: string; name?: string; model?: string };
}

export interface RelatedLiteratureSummary {
  _id: string;
  title: string;
  authors?: string[];
  year?: number;
  journal?: string;
  doi?: string;
}

export interface ExperimentRecord {
  _id: string;
  teamId: string;
  title: string;
  summary: string;
  discipline: ExperimentDiscipline;
  status: ExperimentStatus;
  content: string;
  chemistry?: ChemistryBlock;
  procedureNotes: string;
  /** 已废弃：后端可能仍返回历史数据 */
  customFields?: CustomFieldRow[];
  tags: string[];
  attachments: Attachment[];
  relatedReservations?: RelatedReservationSummary[] | string[];
  relatedLiteratures?: RelatedLiteratureSummary[] | string[];
  createdBy: { _id: string; username?: string; name?: string; email?: string };
  updatedBy: { _id: string; username?: string; name?: string; email?: string };
  createdAt: string;
  updatedAt: string;
}

export interface ExperimentRecordListParams {
  page?: number;
  limit?: number;
  q?: string;
  discipline?: ExperimentDiscipline | '';
  status?: ExperimentStatus | '';
  sortBy?: 'updatedAt' | 'createdAt' | 'title';
  sortOrder?: 'asc' | 'desc';
}

export interface ExperimentRecordPayload {
  title: string;
  summary?: string;
  discipline?: ExperimentDiscipline;
  status?: ExperimentStatus;
  content?: string;
  chemistry?: Partial<ChemistryBlock>;
  procedureNotes?: string;
  tags?: string[];
  attachments?: Attachment[];
  /** 本人仪器预约 _id */
  relatedReservations?: string[];
  /** 当前用户可访问的文献 _id */
  relatedLiteratures?: string[];
}

const emptyChemistry = (): ChemistryBlock => ({
  objective: '',
  procedureOutline: '',
  reagentsAndSolvents: '',
  conditions: '',
  observations: '',
  workupAndAnalysis: '',
  reagentRows: []
});

export const experimentRecordService = {
  getList: async (params: ExperimentRecordListParams = {}) => {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.q && params.q.trim()) qs.set('q', params.q.trim());
    if (params.discipline) qs.set('discipline', params.discipline);
    if (params.status) qs.set('status', params.status);
    if (params.sortBy) qs.set('sortBy', params.sortBy);
    if (params.sortOrder) qs.set('sortOrder', params.sortOrder);

    const res = await apiRequest(`/experiment-records?${qs.toString()}`);
    return res.data as {
      experimentRecords: ExperimentRecord[];
      pagination: { page: number; limit: number; total: number; pages: number };
    };
  },

  getById: async (
    id: string
  ): Promise<{ experimentRecord: ExperimentRecord; canEdit: boolean }> => {
    const res = await apiRequest(`/experiment-records/${id}`);
    const canEdit = res.data.canEdit !== false;
    return {
      experimentRecord: res.data.experimentRecord as ExperimentRecord,
      canEdit: Boolean(canEdit)
    };
  },

  create: async (data: ExperimentRecordPayload): Promise<ExperimentRecord> => {
    const res = await apiRequest('/experiment-records', {
      method: 'POST',
      body: JSON.stringify(data)
    });
    return res.data.experimentRecord as ExperimentRecord;
  },

  update: async (id: string, data: Partial<ExperimentRecordPayload>): Promise<ExperimentRecord> => {
    const res = await apiRequest(`/experiment-records/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
    return res.data.experimentRecord as ExperimentRecord;
  },

  delete: async (id: string): Promise<void> => {
    await apiRequest(`/experiment-records/${id}`, { method: 'DELETE' });
  },

  emptyChemistry
};
